import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';

const STORAGE_KEY = 'stt_test_results';
const SAMPLES_STORAGE_KEY = 'stt_sample_files';

// Default sample files (can be extended via UI)
const DEFAULT_SAMPLES = [
  {
    id: 'sample1',
    language: 'English',
    level: 6,
    sentence: 'notre cabane était très vieille avec une grande fireplace et les murs sont faits du bois',
    filename: 'english-notre-cabane-etait-tres-vieille.wav',
  },
  {
    id: 'sample2',
    language: 'English',
    level: 6,
    sentence: 'GSK veut améliorer la santé de 2,5 milliards de personnes dans les 10 prochaines années',
    filename: 'english-gsk-veut-ameliorer.wav',
  },
  {
    id: 'sample3',
    language: 'English',
    level: 6,
    sentence: 'Ce sont des livres très intéressants',
    filename: 'english-ce-sont-des-livres.wav',
  },
  {
    id: 'sample4',
    language: 'English',
    level: 6,
    sentence: 'Ami',
    filename: 'english-ami.wav',
  },
  {
    id: 'sample5',
    language: 'English',
    level: 4,
    sentence: "J'utilisais les clinical trial pour parler et communiquer les outcomes pour les patients avec aussi le safety pour le patient et utiliser les photo de les patients pour visualiser quelle est l'importance de votre role",
    filename: 'english-clinical-trial.wav',
  },
  {
    id: 'sample6',
    language: 'English',
    level: 4,
    sentence: "je pense que c'est très important de l'industrie euh pharmaceutique dans dans 10 prochaines années de continuer de utiliser les robots les médecins le AI pour vraiment le toutes le médecin de les patients pas juste les médecins pour developper des paths de bien vie et je pense que le rôle de GSK est dans l'oncologie, dans respiratoire et de écouter de qu'est-ce que les physiciens et les patients veux, être vraiment une partenaire",
    filename: 'english-industrie-pharma.wav',
  },
  {
    id: 'sample7',
    language: 'English',
    level: 4,
    sentence: "J'utilisais le data des trials pour provider les examples de quel patient le médecin aide le plus puis je communiquer les side effects que le médecin que le médecin a potentiel de le patient avoir et je l'utilisais des photos de patient pour que l'équipe de vente que imagine quel est le patient qu'il aide et pourquoi c'est important",
    filename: 'english-data-trials.wav',
  },
];

const getSavedSamples = () => {
  try {
    const saved = localStorage.getItem(SAMPLES_STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_SAMPLES;
  } catch {
    return DEFAULT_SAMPLES;
  }
};

// CSV helper functions
const escapeCSV = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const getSavedResults = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

const saveResultsToStorage = (newResults) => {
  try {
    const existing = getSavedResults();
    const updated = [...existing, ...newResults];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated.length;
  } catch (err) {
    console.error('Failed to save results:', err);
    return getSavedResults().length;
  }
};

const clearSavedResults = () => {
  localStorage.removeItem(STORAGE_KEY);
};

// Calculate aggregate WER stats per provider
const calculateAggregateStats = () => {
  const results = getSavedResults();
  if (results.length === 0) return null;

  const stats = {};

  for (const r of results) {
    if (r.wer === null || r.wer === undefined || !r.success) continue;

    const key = r.provider;
    if (!stats[key]) {
      stats[key] = {
        provider: r.provider,
        withContext: { total: 0, count: 0, samples: new Set() },
        withoutContext: { total: 0, count: 0, samples: new Set() },
        withSpeakerProfile: { total: 0, count: 0, samples: new Set() },
      };
    }

    const bucket = r.withSpeakerProfile ? stats[key].withSpeakerProfile
      : r.withContext ? stats[key].withContext
      : stats[key].withoutContext;
    bucket.total += r.wer;
    bucket.count += 1;
    bucket.samples.add(r.sampleFile);
  }

  // Calculate averages
  const aggregated = Object.values(stats).map(s => ({
    provider: s.provider,
    withContext: s.withContext.count > 0
      ? { avg: Math.round((s.withContext.total / s.withContext.count) * 100) / 100, count: s.withContext.count, samples: s.withContext.samples.size }
      : null,
    withoutContext: s.withoutContext.count > 0
      ? { avg: Math.round((s.withoutContext.total / s.withoutContext.count) * 100) / 100, count: s.withoutContext.count, samples: s.withoutContext.samples.size }
      : null,
    withSpeakerProfile: s.withSpeakerProfile.count > 0
      ? { avg: Math.round((s.withSpeakerProfile.total / s.withSpeakerProfile.count) * 100) / 100, count: s.withSpeakerProfile.count, samples: s.withSpeakerProfile.samples.size }
      : null,
  }));

  // Sort by with-context WER (lowest first)
  aggregated.sort((a, b) => {
    const aWer = a.withContext?.avg ?? 999;
    const bWer = b.withContext?.avg ?? 999;
    return aWer - bWer;
  });

  return aggregated;
};

const downloadCSV = () => {
  const results = getSavedResults();
  if (results.length === 0) {
    alert('No results to download');
    return;
  }

  const headers = [
    'timestamp',
    'sample_file',
    'level',
    'provider',
    'with_context',
    'with_speaker_profile',
    'success',
    'transcription',
    'reference_text',
    'wer',
    'duration_ms',
    'native_language',
    'target_language',
    'error',
  ];

  const rows = results.map(r => [
    escapeCSV(r.timestamp),
    escapeCSV(r.sampleFile),
    r.level ?? '',
    escapeCSV(r.provider),
    r.withContext ? 'true' : 'false',
    r.withSpeakerProfile ? 'true' : 'false',
    r.success ? 'true' : 'false',
    escapeCSV(r.transcription),
    escapeCSV(r.referenceText),
    r.wer ?? '',
    r.duration ?? '',
    escapeCSV(r.nativeLanguage),
    escapeCSV(r.targetLanguage),
    escapeCSV(r.error),
  ].join(','));

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `stt_results_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const PROVIDERS = [
  { id: 'conversaflex', name: 'Conversaflex', supportsContext: true },
  { id: 'gemini', name: 'Gemini', supportsContext: true },
  { id: 'gpt-audio', name: 'GPT Audio', supportsContext: true },
  { id: 'whisper', name: 'Whisper', supportsContext: true },
  { id: 'elevenlabs', name: 'ElevenLabs Scribe', supportsContext: false },  // Uses keyterms only, not text prompts
  { id: 'gcs', name: 'Google Cloud Speech', supportsContext: false },  // Uses phrase sets only, not text prompts
  { id: 'deepgram', name: 'Deepgram', supportsContext: false },
];

export default function TranscriptionTester() {
  const [audioSource, setAudioSource] = useState('upload'); // 'upload' or 'sample'
  const [selectedSample, setSelectedSample] = useState('');
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState('English');
  const [targetLanguage, setTargetLanguage] = useState('French');
  const [referenceText, setReferenceText] = useState('');
  const [lastAssistantMessage, setLastAssistantMessage] = useState('');
  const [datasetName, setDatasetName] = useState('transcription-tester');
  const [datasetCount, setDatasetCount] = useState(10);
  const [keyTerms, setKeyTerms] = useState('');  // Comma-separated key terms
  const [selectedProviders, setSelectedProviders] = useState(['conversaflex', 'gemini', 'gpt-audio', 'whisper', 'gcs']);
  const [testWithContext, setTestWithContext] = useState(true);
  const [testWithoutContext, setTestWithoutContext] = useState(true);
  const [testWithSpeakerProfile, setTestWithSpeakerProfile] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [savedCount, setSavedCount] = useState(0);
  const [aggregateStats, setAggregateStats] = useState(null);
  const [sampleFiles, setSampleFiles] = useState([]);
  const [samplesToTest, setSamplesToTest] = useState([]); // List of samples added for testing
  const [samplesFailed, setSamplesFailed] = useState([]); // List of samples failed during testing

  // Load saved results count, aggregate stats, and sample files on mount
  useEffect(() => {
    setSavedCount(getSavedResults().length);
    setAggregateStats(calculateAggregateStats());
    setSampleFiles(getSavedSamples());
  }, []);

  // Update aggregate stats when savedCount changes
  useEffect(() => {
    setAggregateStats(calculateAggregateStats());
  }, [savedCount]);

  const addSampleToTest = () => {
    if (!selectedSample) return;
    // Don't add duplicates
    if (samplesToTest.find(s => s.id === selectedSample)) return;
    const sample = sampleFiles.find(s => s.id === selectedSample);
    if (sample) {
      setSamplesToTest([...samplesToTest, sample]);
      setSelectedSample(''); // Reset dropdown
    }
  };

  const removeSampleFromTest = (id) => {
    setSamplesToTest(samplesToTest.filter(s => s.id !== id));
  };

  const addElementToTest = async () => {
    let datasetFile = file;
    if (!datasetFile) {
      setError('Please select a csv file');
      setLoading(false);
      return;
    }

    // Extract Dataset Elements
    console.log('[Transcribe Tester] Extracting dataset elements...');
    const text = await datasetFile.text();
    const firstLine = text.trim().split(/\r?\n/)[0];
    const separator = firstLine.includes(';') && !firstLine.endsWith(';') ? ';' : ',';
    const rows = text
      .trim()
      .split(/\r?\n/)
      .map(row => row.split(separator))
      .map(row => row.map(cell => cell.replace(/[;\r]/g, '').trim()));
    const headers = rows[0].map(h => h.replace(/[;\r]/g, '').trim()).filter(h => h !== '');
    const elementToTest = rows.slice(1).map(row => {
      return headers.reduce((obj, header, i) => {
        obj[header] = row[i];
        return obj;
      }, {});
    });

    // Add to samplesToTest
    const elementToSample = elementToTest
      .filter(element => element.audio !== undefined)
      .slice(0, datasetCount)
      .map(element => ({
        id: element.id,
        level: null,
        sentence: element.text,
        gcs: element.audio
      }));
    setSamplesToTest(elementToSample);
    console.log(`[Transcribe Tester] Extract ${elementToSample.length} elements.`);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const toggleProvider = (providerId) => {
    setSelectedProviders(prev =>
      prev.includes(providerId)
        ? prev.filter(p => p !== providerId)
        : [...prev, providerId]
    );
  };

  const selectAll = () => setSelectedProviders(PROVIDERS.map(p => p.id));
  const selectNone = () => setSelectedProviders([]);

  // Helper to load a sample file
  const loadSampleFile = async (sample) => {
    const response = await fetch(`/sample_audio/${sample.filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load ${sample.filename}: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const ext = sample.filename.split('.').pop().toLowerCase();
    const mimeTypes = {
      wav: 'audio/wav',
      mp3: 'audio/mpeg',
      m4a: 'audio/mp4',
      ogg: 'audio/ogg',
      webm: 'audio/webm',
      flac: 'audio/flac',
    };
    const mimeType = mimeTypes[ext] || 'audio/wav';
    const blob = new Blob([arrayBuffer], { type: mimeType });
    return new File([blob], sample.filename, { type: mimeType });
  };

  // Helper to load a Google Cloud Storage file
  const loadGCSFile = async (uri) => {
    // /gcs-audio serves super admins only: send the signed-in user's ID token
    const user = getAuth().currentUser;
    if (!user) {
      throw new Error('Sign in with a super admin account to load dataset audio');
    }
    const idToken = await user.getIdToken();

    // !!! TEMPORARILY using netlify url !!!
    const response = await fetch(`/.netlify/functions/gcs-audio?uri=${encodeURI(uri)}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.ok) {
      throw new Error(`Failed to load ${uri}: ${response.status}`);
    }
    const data = await response.json();
    const byteArray = Uint8Array.from(atob(data.content), c => c.charCodeAt(0));
    
    const filename = uri.split('/').pop();
    const ext = uri.split('.').pop().toLowerCase();
    const mimeTypes = {
      wav: 'audio/wav',
      mp3: 'audio/mpeg',
      m4a: 'audio/mp4',
      ogg: 'audio/ogg',
      webm: 'audio/webm',
      flac: 'audio/flac',
    };
    const mimeType = mimeTypes[ext] || 'audio/wav';
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  };

  // Helper to run transcription for a single file
  const runTranscription = async (audioFile, refText, datasetName, datasetSample) => {
    const formData = new FormData();
    formData.append('file', audioFile);
    formData.append('language', language);
    formData.append('targetLanguage', targetLanguage);
    formData.append('provider', selectedProviders.join(','));
    formData.append('withContext', testWithContext.toString());
    formData.append('withoutContext', testWithoutContext.toString());
    formData.append('withSpeakerProfile', testWithSpeakerProfile.toString());
    if (refText.trim()) {
      formData.append('referenceText', refText.trim());
    }
    if (datasetName.trim()) {
      formData.append('datasetName', datasetName.trim());
    }
    if (datasetSample.trim()) {
      formData.append('datasetItemName', datasetSample.trim());
    }
    if (lastAssistantMessage.trim()) {
      formData.append('lastAssistantMessage', lastAssistantMessage.trim());
    }
    if (keyTerms.trim()) {
      formData.append('keyTerms', keyTerms.trim());
    }

    const response = await fetch('/transcribe', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);
    setSamplesFailed([]);

    try {
      const allSamplesFailed = [];
      const allRowsToSave = [];
      let allResults = {};

      if ((audioSource === 'sample' || audioSource === 'dataset') && samplesToTest.length > 0) {
        // Test multiple samples
        let sttPrompt = null;
        for (const sample of samplesToTest) {
          try {
            let audioFile;
            if (audioSource === 'dataset') {
              audioFile = await loadGCSFile(sample.gcs);
              sample.filename = audioFile.name;
            } else {
              audioFile = await loadSampleFile(sample);
            }
            const data = await runTranscription(audioFile, sample.sentence, datasetName, sample.filename);
            const timestamp = new Date().toISOString();

            // Capture sttPrompt from first response
            if (!sttPrompt && data.config?.sttPrompt) {
              sttPrompt = data.config.sttPrompt;
            }

            // Filter and merge results
            for (const providerId of selectedProviders) {
              if (data.results[providerId]) {
                if (!allResults[providerId]) {
                  allResults[providerId] = { samples: [], supportsContext: data.results[providerId].supportsContext };
                }
                allResults[providerId].samples.push({
                  sample: sample.filename,
                  level: sample.level,
                  sentence: sample.sentence,
                  ...data.results[providerId],
                });

                // Save rows for CSV
                if (data.results[providerId].withContext) {
                  allRowsToSave.push({
                    timestamp,
                    sampleFile: sample.filename,
                    level: sample.level,
                    provider: providerId,
                    withContext: true,
                    withSpeakerProfile: false,
                    success: data.results[providerId].withContext.success,
                    transcription: data.results[providerId].withContext.text || '',
                    referenceText: sample.sentence,
                    wer: data.results[providerId].withContext.wer?.wer ?? null,
                    duration: data.results[providerId].withContext.duration,
                    nativeLanguage: language,
                    targetLanguage,
                    error: data.results[providerId].withContext.error || '',
                  });
                }
                if (data.results[providerId].withoutContext) {
                  allRowsToSave.push({
                    timestamp,
                    sampleFile: sample.filename,
                    level: sample.level,
                    provider: providerId,
                    withContext: false,
                    withSpeakerProfile: false,
                    success: data.results[providerId].withoutContext.success,
                    transcription: data.results[providerId].withoutContext.text || '',
                    referenceText: sample.sentence,
                    wer: data.results[providerId].withoutContext.wer?.wer ?? null,
                    duration: data.results[providerId].withoutContext.duration,
                    nativeLanguage: language,
                    targetLanguage,
                    error: data.results[providerId].withoutContext.error || '',
                  });
                }
                if (data.results[providerId].withSpeakerProfile) {
                  allRowsToSave.push({
                    timestamp,
                    sampleFile: sample.filename,
                    level: sample.level,
                    provider: providerId,
                    withContext: true,
                    withSpeakerProfile: true,
                    success: data.results[providerId].withSpeakerProfile.success,
                    transcription: data.results[providerId].withSpeakerProfile.text || '',
                    referenceText: sample.sentence,
                    wer: data.results[providerId].withSpeakerProfile.wer?.wer ?? null,
                    duration: data.results[providerId].withSpeakerProfile.duration,
                    nativeLanguage: language,
                    targetLanguage,
                    error: data.results[providerId].withSpeakerProfile.error || '',
                  });
                }
              }
            }
          } catch (err) {
            allSamplesFailed.push({
              sampleFile: sample.filename,
              error: err
            });
            console.error(`Error testing ${sample.filename}:`, err);
          }
        }
        setResults({
          results: allResults,
          multiSample: true,
          config: { sttPrompt, language, targetLanguage },
        });
        setSamplesFailed(allSamplesFailed);
      } else {
        // Single file test (upload or single sample)
        let audioFile = file;
        let refText = referenceText;
        let currentSample = null;

        if (audioSource === 'upload') {
          if (!file) {
            setError('Please select an audio file');
            setLoading(false);
            return;
          }
        } else {
          setError('Please add at least one sample to test');
          setLoading(false);
          return;
        }

        const data = await runTranscription(audioFile, refText, datasetName, audioFile.name);
        const timestamp = new Date().toISOString();

        // Filter results to only selected providers
        const filteredResults = {};
        for (const providerId of selectedProviders) {
          if (data.results[providerId]) {
            filteredResults[providerId] = data.results[providerId];
          }
        }
        data.results = filteredResults;
        setResults(data);

        const sampleFile = currentSample?.filename || file?.name || 'uploaded';
        const sampleLevel = currentSample?.level ?? null;

        for (const [providerId, providerData] of Object.entries(filteredResults)) {
          if (providerData.withContext) {
            allRowsToSave.push({
              timestamp,
              sampleFile,
              level: sampleLevel,
              provider: providerId,
              withContext: true,
              withSpeakerProfile: false,
              success: providerData.withContext.success,
              transcription: providerData.withContext.text || '',
              referenceText: refText.trim(),
              wer: providerData.withContext.wer?.wer ?? null,
              duration: providerData.withContext.duration,
              nativeLanguage: language,
              targetLanguage,
              error: providerData.withContext.error || '',
            });
          }
          if (providerData.withoutContext) {
            allRowsToSave.push({
              timestamp,
              sampleFile,
              level: sampleLevel,
              provider: providerId,
              withContext: false,
              withSpeakerProfile: false,
              success: providerData.withoutContext.success,
              transcription: providerData.withoutContext.text || '',
              referenceText: refText.trim(),
              wer: providerData.withoutContext.wer?.wer ?? null,
              duration: providerData.withoutContext.duration,
              nativeLanguage: language,
              targetLanguage,
              error: providerData.withoutContext.error || '',
            });
          }
          if (providerData.withSpeakerProfile) {
            allRowsToSave.push({
              timestamp,
              sampleFile,
              level: sampleLevel,
              provider: providerId,
              withContext: true,
              withSpeakerProfile: true,
              success: providerData.withSpeakerProfile.success,
              transcription: providerData.withSpeakerProfile.text || '',
              referenceText: refText.trim(),
              wer: providerData.withSpeakerProfile.wer?.wer ?? null,
              duration: providerData.withSpeakerProfile.duration,
              nativeLanguage: language,
              targetLanguage,
              error: providerData.withSpeakerProfile.error || '',
            });
          }
        }
      }

      if (allRowsToSave.length > 0) {
        const newCount = saveResultsToStorage(allRowsToSave);
        setSavedCount(newCount);
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const getWERColor = (wer) => {
    if (wer === null || wer === undefined) return '#666';
    if (wer <= 10) return '#22c55e';
    if (wer <= 25) return '#eab308';
    return '#ef4444';
  };

  return (
    <div style={{ maxWidth: 1200, margin: '24px auto', padding: '16px', backgroundColor: '#fff', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ color: '#1f2937', margin: 0 }}>STT Model Tester</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: '#6b7280', fontSize: 14 }}>{savedCount} results saved</span>
          <button
            type="button"
            onClick={downloadCSV}
            disabled={savedCount === 0}
            style={{ padding: '6px 12px', fontSize: 14, cursor: savedCount === 0 ? 'not-allowed' : 'pointer' }}
          >
            Download CSV
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Clear all saved results?')) {
                clearSavedResults();
                setSavedCount(0);
              }
            }}
            disabled={savedCount === 0}
            style={{ padding: '6px 12px', fontSize: 14, cursor: savedCount === 0 ? 'not-allowed' : 'pointer' }}
          >
            Clear
          </button>
        </div>
      </div>
      <p style={{ color: '#4b5563' }}>Compare speech-to-text providers with/without language context. Upload an audio file and see transcription results side by side.</p>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
        {/* Audio source selection */}
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
            Audio Source
          </label>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#374151' }}>
              <input
                type="radio"
                name="audioSource"
                value="upload"
                checked={audioSource === 'upload'}
                onChange={(e) => setAudioSource(e.target.value)}
              />
              <span>Upload File</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#374151' }}>
              <input
                type="radio"
                name="audioSource"
                value="sample"
                checked={audioSource === 'sample'}
                onChange={(e) => setAudioSource(e.target.value)}
              />
              <span>Sample Files</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#374151' }}>
              <input
                type="radio"
                name="audioSource"
                value="dataset"
                checked={audioSource === 'dataset'}
                onChange={(e) => setAudioSource(e.target.value)}
              />
              <span>Upload Dataset CSV</span>
            </label>
          </div>

          {audioSource === 'upload' ? (
            <input
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              style={{ width: '100%' }}
            />
          ) : audioSource === 'dataset' ? (
            <>
            <div>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}></div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
                Dataset Name used in LangFuse Datasets
              </label>
              <input
                type="text"
                value={datasetName}
                onChange={(e) => setDatasetName(e.target.value)}
                style={{ width: '100%', padding: 8 }}
                placeholder="e.g., transcription-tester-run-2026-05-27"
              />
            </div>

            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
                Select max sample count
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={datasetCount}
                onChange={(e) => setDatasetCount(parseInt(e.target.value)) || 0}
                style={{ flex: '0 0 25%', padding: 8 }}
                placeholder="e.g., 10"
              />
              <button
                type="button"
                onClick={addElementToTest}
                style={{ padding: '8px 16px', cursor: file ? 'pointer' : 'not-allowed' }}
              >
                Extract the dataset samples before testing
              </button>
              </div>
            </div>
            </>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={selectedSample}
                  onChange={(e) => setSelectedSample(e.target.value)}
                  style={{ flex: 1, padding: 8 }}
                >
                  <option value="">-- Select a sample --</option>
                  {sampleFiles.filter(s => !samplesToTest.find(t => t.id === s.id)).map(sample => (
                    <option key={sample.id} value={sample.id}>
                      L{sample.level || '?'}: {sample.sentence.substring(0, 50)}{sample.sentence.length > 50 ? '...' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addSampleToTest}
                  disabled={!selectedSample}
                  style={{ padding: '8px 16px', cursor: selectedSample ? 'pointer' : 'not-allowed' }}
                >
                  Add
                </button>
              </div>

              {/* List of samples to test */}
              {samplesToTest.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Samples to test ({samplesToTest.length}):</div>
                  {samplesToTest.map(sample => (
                    <div
                      key={sample.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 8px',
                        background: '#f0fdf4',
                        marginBottom: 4,
                        borderRadius: 4,
                        border: '1px solid #bbf7d0',
                      }}
                    >
                      <span style={{ color: '#374151', fontSize: 13 }}>
                        <strong>L{sample.level || '?'}</strong>: {sample.sentence.substring(0, 45)}...
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSampleFromTest(sample.id)}
                        style={{ padding: '2px 8px', fontSize: 12, color: '#dc2626', background: 'transparent', border: 'none', cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Language settings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
              Native Language
            </label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} style={{ width: '100%', padding: 8 }}>
              <option value="English">English</option>
              <option value="French">French</option>
              <option value="Spanish">Spanish</option>
              <option value="German">German</option>
              <option value="Mandarin">Mandarin</option>
              <option value="Arabic">Arabic</option>
              <option value="Portuguese">Portuguese</option>
              <option value="Italian">Italian</option>
              <option value="Vietnamese">Vietnamese</option>
              <option value="Hindi">Hindi</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
              Target Language (learning)
            </label>
            <select value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)} style={{ width: '100%', padding: 8 }}>
              <option value="French">French</option>
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
              <option value="German">German</option>
              <option value="Mandarin">Mandarin</option>
              <option value="Italian">Italian</option>
              <option value="Portuguese">Portuguese</option>
            </select>
          </div>
        </div>

        {/* Reference text - only for upload mode */}
        {audioSource === 'upload' && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
              Reference Text (optional, for WER calculation)
            </label>
            <textarea
              value={referenceText}
              onChange={(e) => setReferenceText(e.target.value)}
              style={{ width: '100%', minHeight: 80, padding: 8 }}
              placeholder="Enter the ground truth transcription to calculate Word Error Rate"
            />
          </div>
        )}

        {/* Provider selection */}
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
            Providers to Test
          </label>
          <div style={{ marginBottom: 8 }}>
            <button type="button" onClick={selectAll} style={{ marginRight: 8 }}>Select All</button>
            <button type="button" onClick={selectNone}>Select None</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {PROVIDERS.map(provider => (
              <label key={provider.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={selectedProviders.includes(provider.id)}
                  onChange={() => toggleProvider(provider.id)}
                />
                <span>{provider.name}</span>
                {provider.supportsContext && (
                  <span style={{ fontSize: 10, color: '#6b7280' }}>(ctx)</span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Context testing options */}
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
            Test Options
          </label>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#374151' }}>
              <input
                type="checkbox"
                checked={testWithContext}
                onChange={(e) => setTestWithContext(e.target.checked)}
              />
              <span>With language context</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#374151' }}>
              <input
                type="checkbox"
                checked={testWithoutContext}
                onChange={(e) => setTestWithoutContext(e.target.checked)}
              />
              <span>Without language context</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#374151' }}>
              <input
                type="checkbox"
                checked={testWithSpeakerProfile}
                onChange={(e) => setTestWithSpeakerProfile(e.target.checked)}
              />
              <span>With context + speaker profile</span>
            </label>
          </div>
        </div>

        {/* Last assistant message - only when testing with context */}
        {testWithContext && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
              Last Assistant Message (optional, for response context)
            </label>
            <input
              type="text"
              value={lastAssistantMessage}
              onChange={(e) => setLastAssistantMessage(e.target.value)}
              style={{ width: '100%', padding: 8 }}
              placeholder="e.g., Comment puis-je vous aider aujourd'hui?"
            />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              Helps STT understand what the user is responding to
            </div>
          </div>
        )}

        {/* Key terms - only when testing with context */}
        {testWithContext && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#1f2937' }}>
              Key Terms (optional, comma-separated)
            </label>
            <input
              type="text"
              value={keyTerms}
              onChange={(e) => setKeyTerms(e.target.value)}
              style={{ width: '100%', padding: 8 }}
              placeholder="e.g., GSK, clinical trial, oncologie, médicament"
            />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              Domain-specific terms, company names, or vocabulary the user might say
            </div>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={loading || selectedProviders.length === 0 || (audioSource === 'sample' && samplesToTest.length === 0) || (audioSource === 'dataset' && samplesToTest.length === 0)}
            style={{ padding: '12px 24px', fontSize: 16, cursor: loading ? 'wait' : 'pointer' }}
          >
            {loading
              ? `Testing${samplesToTest.length > 0 ? ` ${samplesToTest.length} samples` : ''}...`
              : (audioSource === 'sample' || audioSource === 'dataset') && samplesToTest.length > 0
                ? `Test ${samplesToTest.length} Sample${samplesToTest.length !== 1 ? 's' : ''} × ${selectedProviders.length} Provider${selectedProviders.length !== 1 ? 's' : ''}`
                : `Test ${selectedProviders.length} Provider${selectedProviders.length !== 1 ? 's' : ''}`
            }
          </button>
        </div>
      </form>

      {error && (
        <div style={{ padding: 16, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>
          Error: {error}
        </div>
      )}

      {results && (
        <div>
          <h3 style={{ color: '#1f2937' }}>Results {results.multiSample && `(${samplesToTest.length} samples -- ${samplesFailed?.length} samples failed)`}</h3>

          {/* Display STT Context Prompt */}
          {results.config?.sttPrompt && (
            <div style={{
              padding: 16,
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: 8,
              marginBottom: 16
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#166534' }}>
                STT Context Prompt (sent to providers that support it):
              </div>
              <div style={{
                fontFamily: 'monospace',
                fontSize: 13,
                color: '#15803d',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {results.config.sttPrompt}
              </div>
            </div>
          )}

          {results.multiSample ? (
            // Multi-sample results display
            <div style={{ overflowX: 'auto' }}>
              {Object.entries(results.results).map(([providerId, data]) => {
                const providerSupportsContext = data.supportsContext !== false;

                return (
                  <div key={providerId} style={{ marginBottom: 24 }}>
                    <h4 style={{ color: '#1f2937', marginBottom: 8 }}>
                      {PROVIDERS.find(p => p.id === providerId)?.name || providerId}
                      {!providerSupportsContext && (
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 'normal', marginLeft: 8 }}>
                          (does not support context)
                        </span>
                      )}
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, backgroundColor: '#fff' }}>
                      <thead>
                        <tr style={{ background: '#374151' }}>
                          <th style={{ padding: 8, textAlign: 'left', color: '#fff' }}>Sample</th>
                          {testWithoutContext && <th style={{ padding: 8, textAlign: 'left', color: '#fff' }}>Without Context</th>}
                          {testWithContext && <th style={{ padding: 8, textAlign: 'left', color: '#fff' }}>With Context</th>}
                          {testWithSpeakerProfile && <th style={{ padding: 8, textAlign: 'left', color: '#fff', background: '#1e3a5f' }}>Context + Speaker Profile</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {data.samples?.map((sampleData, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: 8, verticalAlign: 'top', color: '#1f2937', maxWidth: 200 }}>
                              <div style={{ fontWeight: 'bold' }}>L{sampleData.level}: {sampleData.sentence.substring(0, 30)}...</div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>{sampleData.sample}</div>
                            </td>
                            {testWithoutContext && (
                              <td style={{ padding: 8, verticalAlign: 'top', color: '#1f2937' }}>
                                {(() => {
                                  const resultData = providerSupportsContext
                                    ? sampleData.withoutContext
                                    : (sampleData.withoutContext || sampleData.withContext);
                                  if (!resultData) return <span style={{ color: '#9ca3af' }}>—</span>;
                                  if (resultData.success) {
                                    return (
                                      <div>
                                        <div style={{ marginBottom: 4 }}>{resultData.text}</div>
                                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                                          {resultData.duration}ms
                                          {resultData.wer && (
                                            <span style={{ marginLeft: 8, color: getWERColor(resultData.wer.wer) }}>
                                              WER: {resultData.wer.wer}%
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return <div style={{ color: '#dc2626', fontSize: 12 }}>Error: {resultData.error}</div>;
                                })()}
                              </td>
                            )}
                            {testWithContext && (
                              <td style={{ padding: 8, verticalAlign: 'top', color: '#1f2937' }}>
                                {providerSupportsContext && sampleData.withContext ? (
                                  sampleData.withContext.success ? (
                                    <div>
                                      <div style={{ marginBottom: 4 }}>{sampleData.withContext.text}</div>
                                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                                        {sampleData.withContext.duration}ms
                                        {sampleData.withContext.wer && (
                                          <span style={{ marginLeft: 8, color: getWERColor(sampleData.withContext.wer.wer) }}>
                                            WER: {sampleData.withContext.wer.wer}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ color: '#dc2626', fontSize: 12 }}>Error: {sampleData.withContext.error}</div>
                                  )
                                ) : <span style={{ color: '#9ca3af' }}>—</span>}
                              </td>
                            )}
                            {testWithSpeakerProfile && (
                              <td style={{ padding: 8, verticalAlign: 'top', color: '#1f2937', background: '#f0f7ff' }}>
                                {providerSupportsContext && sampleData.withSpeakerProfile ? (
                                  sampleData.withSpeakerProfile.success ? (
                                    <div>
                                      <div style={{ marginBottom: 4 }}>{sampleData.withSpeakerProfile.text}</div>
                                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                                        {sampleData.withSpeakerProfile.duration}ms
                                        {sampleData.withSpeakerProfile.wer && (
                                          <span style={{ marginLeft: 8, color: getWERColor(sampleData.withSpeakerProfile.wer.wer) }}>
                                            WER: {sampleData.withSpeakerProfile.wer.wer}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ color: '#dc2626', fontSize: 12 }}>Error: {sampleData.withSpeakerProfile.error}</div>
                                  )
                                ) : <span style={{ color: '#9ca3af' }}>—</span>}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            // Single file results display
            <>
              <p style={{ color: '#4b5563', marginBottom: 16 }}>
                Native: {results.config?.language} → Target: {results.config?.targetLanguage}
                {results.config?.referenceText && ` | Reference: "${results.config.referenceText}"`}
              </p>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, backgroundColor: '#fff' }}>
                  <thead>
                    <tr style={{ background: '#1f2937' }}>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #374151', color: '#fff' }}>Provider</th>
                      {testWithoutContext && (
                        <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #374151', color: '#fff' }}>Without Context</th>
                      )}
                      {testWithContext && (
                        <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #374151', color: '#fff' }}>With Context</th>
                      )}
                      {testWithSpeakerProfile && (
                        <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #374151', color: '#fff', background: '#1e3a5f' }}>Context + Speaker Profile</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(results.results).map(([providerId, data]) => {
                      const providerSupportsContext = data.supportsContext !== false;

                      return (
                        <tr key={providerId} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
                          <td style={{ padding: 12, verticalAlign: 'top', fontWeight: 'bold', color: '#1f2937' }}>
                            {data.name}
                            {!providerSupportsContext && (
                              <span style={{ display: 'block', fontSize: 10, color: '#6b7280', fontWeight: 'normal' }}>
                                No context support
                              </span>
                            )}
                          </td>
                          {testWithoutContext && (
                            <td style={{ padding: 12, verticalAlign: 'top', color: '#1f2937' }}>
                              {(() => {
                                // For providers without context support, show their result in this column
                                const resultData = providerSupportsContext
                                  ? data.withoutContext
                                  : (data.withoutContext || data.withContext);
                                if (!resultData) return <span style={{ color: '#9ca3af' }}>—</span>;
                                if (resultData.success) {
                                  return (
                                    <div>
                                      <div style={{ marginBottom: 8, color: '#1f2937' }}>{resultData.text}</div>
                                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                                        {resultData.duration}ms
                                        {resultData.wer && (
                                          <span style={{ marginLeft: 8, color: getWERColor(resultData.wer.wer) }}>
                                            WER: {resultData.wer.wer}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                return <div style={{ color: '#dc2626' }}>Error: {resultData.error}</div>;
                              })()}
                            </td>
                          )}
                          {testWithContext && (
                            <td style={{ padding: 12, verticalAlign: 'top', color: '#1f2937' }}>
                              {providerSupportsContext && data.withContext ? (
                                data.withContext.success ? (
                                  <div>
                                    <div style={{ marginBottom: 8, color: '#1f2937' }}>{data.withContext.text}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                      {data.withContext.duration}ms
                                      {data.withContext.wer && (
                                        <span style={{ marginLeft: 8, color: getWERColor(data.withContext.wer.wer) }}>
                                          WER: {data.withContext.wer.wer}%
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ color: '#dc2626' }}>Error: {data.withContext.error}</div>
                                )
                              ) : (
                                <span style={{ color: '#9ca3af' }}>—</span>
                              )}
                            </td>
                          )}
                          {testWithSpeakerProfile && (
                            <td style={{ padding: 12, verticalAlign: 'top', color: '#1f2937', background: '#f0f7ff' }}>
                              {providerSupportsContext && data.withSpeakerProfile ? (
                                data.withSpeakerProfile.success ? (
                                  <div>
                                    <div style={{ marginBottom: 8, color: '#1f2937' }}>{data.withSpeakerProfile.text}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                      {data.withSpeakerProfile.duration}ms
                                      {data.withSpeakerProfile.wer && (
                                        <span style={{ marginLeft: 8, color: getWERColor(data.withSpeakerProfile.wer.wer) }}>
                                          WER: {data.withSpeakerProfile.wer.wer}%
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ color: '#dc2626' }}>Error: {data.withSpeakerProfile.error}</div>
                                )
                              ) : (
                                <span style={{ color: '#9ca3af' }}>—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ marginTop: 24 }}>
            <h4 style={{ color: '#1f2937' }}>WER Legend</h4>
            <div style={{ display: 'flex', gap: 16, fontSize: 14, color: '#4b5563' }}>
              <span><span style={{ color: '#22c55e' }}>●</span> ≤10% (Excellent)</span>
              <span><span style={{ color: '#eab308' }}>●</span> 11-25% (Good)</span>
              <span><span style={{ color: '#ef4444' }}>●</span> &gt;25% (Needs improvement)</span>
            </div>
          </div>
        </div>
      )}

      {/* Aggregate WER Stats */}
      {aggregateStats && aggregateStats.length > 0 && (
        <div style={{ marginTop: 32, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <h3 style={{ color: '#1f2937', marginTop: 0 }}>Overall WER by Provider</h3>
          <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
            Average Word Error Rate across all {savedCount} saved test results
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#1f2937' }}>
                <th style={{ padding: 10, textAlign: 'left', color: '#fff' }}>Provider</th>
                <th style={{ padding: 10, textAlign: 'center', color: '#fff' }}>Without Context</th>
                <th style={{ padding: 10, textAlign: 'center', color: '#fff' }}>With Context</th>
                <th style={{ padding: 10, textAlign: 'center', color: '#fff', background: '#1e3a5f' }}>Context + Speaker Profile</th>
                <th style={{ padding: 10, textAlign: 'center', color: '#fff' }}>Context vs No Context</th>
                <th style={{ padding: 10, textAlign: 'center', color: '#fff' }}>Profile vs Context</th>
              </tr>
            </thead>
            <tbody>
              {aggregateStats.map(stat => {
                const contextDiff = stat.withContext && stat.withoutContext
                  ? Math.round((stat.withoutContext.avg - stat.withContext.avg) * 100) / 100
                  : null;
                const profileDiff = stat.withSpeakerProfile && stat.withContext
                  ? Math.round((stat.withContext.avg - stat.withSpeakerProfile.avg) * 100) / 100
                  : null;
                return (
                  <tr key={stat.provider} style={{ borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
                    <td style={{ padding: 10, fontWeight: 'bold', color: '#1f2937' }}>
                      {PROVIDERS.find(p => p.id === stat.provider)?.name || stat.provider}
                    </td>
                    <td style={{ padding: 10, textAlign: 'center' }}>
                      {stat.withoutContext ? (
                        <span style={{ color: getWERColor(stat.withoutContext.avg) }}>
                          {stat.withoutContext.avg}%
                          <span style={{ color: '#9ca3af', fontSize: 11 }}> ({stat.withoutContext.count} tests, {stat.withoutContext.samples} samples)</span>
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 10, textAlign: 'center' }}>
                      {stat.withContext ? (
                        <span style={{ color: getWERColor(stat.withContext.avg) }}>
                          {stat.withContext.avg}%
                          <span style={{ color: '#9ca3af', fontSize: 11 }}> ({stat.withContext.count} tests, {stat.withContext.samples} samples)</span>
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 10, textAlign: 'center', background: '#f0f7ff' }}>
                      {stat.withSpeakerProfile ? (
                        <span style={{ color: getWERColor(stat.withSpeakerProfile.avg) }}>
                          {stat.withSpeakerProfile.avg}%
                          <span style={{ color: '#9ca3af', fontSize: 11 }}> ({stat.withSpeakerProfile.count} tests, {stat.withSpeakerProfile.samples} samples)</span>
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 10, textAlign: 'center' }}>
                      {contextDiff !== null ? (
                        <span style={{ color: contextDiff > 0 ? '#22c55e' : contextDiff < 0 ? '#ef4444' : '#6b7280' }}>
                          {contextDiff > 0 ? '+' : ''}{contextDiff}%
                          {contextDiff > 0 && <span style={{ fontSize: 11 }}> better</span>}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 10, textAlign: 'center' }}>
                      {profileDiff !== null ? (
                        <span style={{ color: profileDiff > 0 ? '#22c55e' : profileDiff < 0 ? '#ef4444' : '#6b7280' }}>
                          {profileDiff > 0 ? '+' : ''}{profileDiff}%
                          {profileDiff > 0 && <span style={{ fontSize: 11 }}> better</span>}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
